import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.currentTarget;
      const formDataToSend = new URLSearchParams();
      
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d608nlg3qj6k6vba3m7g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          industry: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-navy via-navy/95 to-navy/90 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Get Started with PointCalls
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Stop losing revenue to missed calls. Let's discuss how our AI phone agent can transform your business.
            </p>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <h2 className="text-3xl font-bold text-navy mb-6">
                  Let's Talk About Your Needs
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Schedule a 30-minute demo to see CallCatcher in action, or call our live demo line to experience it yourself.
                </p>

                <div className="space-y-6 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-light rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-teal"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Call Our Live Demo</h3>
                      <a href="tel:+16787376473" className="text-teal hover:text-teal/80 transition-colors cursor-pointer">
                        +1 (678) 737-6473
                      </a>
                      <p className="text-sm text-gray-600 mt-1">Experience our AI agent firsthand</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-light rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-2xl text-teal"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Email Us</h3>
                      <a href="mailto:support@pointcalls.com" className="text-teal hover:text-teal/80 transition-colors cursor-pointer">
                        support@pointcalls.com
                      </a>
                      <p className="text-sm text-gray-600 mt-1">We respond within 4 business hours</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-light rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-2xl text-teal"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Business Hours</h3>
                      <p className="text-gray-700">Monday - Friday: 9am - 6pm EST</p>
                      <p className="text-sm text-gray-600 mt-1">Our AI demo line is available 24/7</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-teal-light to-white rounded-2xl p-6 border border-teal/20">
                  <h3 className="font-semibold text-navy mb-3">What to Expect</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-teal mt-0.5"></i>
                      <span>30-minute personalized demo</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-teal mt-0.5"></i>
                      <span>Discussion of your specific call flows</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-teal mt-0.5"></i>
                      <span>Custom pricing based on call volume</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-teal mt-0.5"></i>
                      <span>No commitment required</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-navy mb-6">Request a Demo</h3>
                
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent outline-none transition-all text-base"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent outline-none transition-all text-base"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent outline-none transition-all text-base"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent outline-none transition-all text-base"
                      placeholder="Your Company"
                    />
                  </div>

                  <div>
                    <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
                      Industry *
                    </label>
                    <select
                      id="industry"
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent outline-none transition-all text-base cursor-pointer"
                    >
                      <option value="">Select your industry</option>
                      <option value="Law Firms">Law Firms</option>
                      <option value="Real Estate">Real Estate</option>
                      <option value="Roofing">Roofing</option>
                      <option value="Recruiting">Recruiting & HR</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Tell us about your needs
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent outline-none transition-all resize-none text-base"
                      placeholder="Tell us about your call volume, current challenges, and what you're looking for..."
                    />
                    <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-teal text-white font-semibold rounded-lg hover:bg-teal/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Demo'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                      <i className="ri-check-circle-line text-2xl text-green-600 mb-2"></i>
                      <p className="text-green-800 font-medium">Thank you! We'll be in touch within 4 business hours.</p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                      <i className="ri-error-warning-line text-2xl text-red-600 mb-2"></i>
                      <p className="text-red-800 font-medium">Something went wrong. Please try again or call us directly.</p>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">
                Join Hundreds of Businesses
              </h2>
              <p className="text-lg text-gray-600">
                Already capturing every opportunity with CallCatcher
              </p>
            </div>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-teal mb-2">94%</div>
                <p className="text-gray-600">Calls Answered</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-teal mb-2">$180K+</div>
                <p className="text-gray-600">Avg. Monthly Revenue Saved</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-teal mb-2">24/7</div>
                <p className="text-gray-600">Always Available</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-teal mb-2">2 weeks</div>
                <p className="text-gray-600">Average Setup Time</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;